import {
  AccountsService,
  CancelPlanNowResponse,
  GetCancelCouponOfferResponse,
  GetPausePlanOfferResponse,
} from 'base/accounts_service';
import { UnreachableError } from 'base/helpers';
import { Button } from 'base/ui/button';
import { Checkbox } from 'base/ui/checkbox';
import { Dialog } from 'base/ui/dialog';
import { InvisibleForm, RedErrorText, useSubmit } from 'base/ui/form';
import { IconPause } from 'base/ui/icons';
import { Inline, Rows, SpacerGrow } from 'base/ui/layout';
import { LoadingDots } from 'base/ui/loading_dots';
import { Textarea } from 'base/ui/textarea';
import {
  TextRegular,
  TextRegularLight,
  TextSmall,
  TextSmallLight,
} from 'base/ui/typography';
import { DiscountChip } from 'blocks/manage_account/ui';
import classNames from 'classnames';
import React from 'react';
import { AcceptPlanDialogUrlState } from './accept_plan_offer';
import styles from './cancel_plan.css';

export function CancelPlanDialog({
  accountsService,
  onClose,
  onDone,
  onUpsell,
}: {
  accountsService: Pick<
    AccountsService,
    | 'getPausePlanOffer'
    | 'acceptPausePlanOffer'
    | 'saveChurnFeedback'
    | 'getCancelCouponOffer'
    | 'acceptCancelCouponOffer'
    | 'cancelPlanNow'
  >;
  onClose: () => void;
  onDone: () => void;
  onUpsell: (state: AcceptPlanDialogUrlState) => void;
}) {
  const [submittedFeedback, setSubmittedFeedback] = React.useState(false);
  const [rejectedPause, setRejectedPause] = React.useState(false);
  const [rejectedCancelOffers, setRejectedCancelOffers] = React.useState(false);

  const cancelOffers = useCancelOffers(accountsService);
  const pauseOffers = usePausePlanOffer(accountsService);

  // 1. Pause plan (if available)
  if (
    !rejectedPause &&
    (pauseOffers.value == null || pauseOffers.value.offerPause)
  ) {
    if (pauseOffers.error) return <ErrorDialog error={pauseOffers.error} />;
    if (!pauseOffers.value) return <LoadingDialog title="Loading next step" />;
    return (
      <PausePlanDialog
        offer={pauseOffers.value}
        accountsService={accountsService}
        onDone={onDone}
        onClose={onClose}
        onReject={() => setRejectedPause(true)}
      />
    );
  }
  // 2. Require feedback to continue
  if (!submittedFeedback) {
    return (
      <ChurnFeedbackDialog
        accountsService={accountsService}
        onDone={() => setSubmittedFeedback(true)}
        onClose={onClose}
      />
    );
  }
  // 3. Discount offers (if available)
  if (!rejectedCancelOffers && showCancelOffersStep(cancelOffers.value)) {
    if (cancelOffers.error) return <ErrorDialog error={cancelOffers.error} />;
    if (!cancelOffers.value) return <LoadingDialog title="Loading next step" />;
    return (
      <CancelDiscountOfferDialog
        accountsService={accountsService}
        onDone={onDone}
        onClose={onClose}
        onUpsell={onUpsell}
        onReject={() => setRejectedCancelOffers(true)}
        offers={cancelOffers.value}
      />
    );
  }
  // 4. Actually cancel plan
  return (
    <CancelNowOnMountDialog accountsService={accountsService} onDone={onDone} />
  );
}

function showCancelOffersStep(
  offers: GetCancelCouponOfferResponse | undefined,
) {
  if (offers == null) {
    return true; // show loading state
  }
  return offers.discountOfferPercent != null || offers.mtyOffer != null;
}

function useCancelOffers(
  accountsService: Pick<AccountsService, 'getCancelCouponOffer'>,
) {
  const [value, setValue] = React.useState<
    GetCancelCouponOfferResponse | undefined
  >();
  const [error, setError] = React.useState<unknown>();
  React.useEffect(() => {
    accountsService.getCancelCouponOffer().then(setValue).catch(setError);
  }, [accountsService]);
  return { value, error };
}

function usePausePlanOffer(
  accountsService: Pick<AccountsService, 'getPausePlanOffer'>,
) {
  const [value, setValue] = React.useState<
    GetPausePlanOfferResponse | undefined
  >();
  const [error, setError] = React.useState<unknown>();
  React.useEffect(() => {
    accountsService.getPausePlanOffer().then(setValue).catch(setError);
  }, [accountsService]);
  return { value, error };
}

export function PausePlanDialog({
  offer: { currentPeriodEndHuman, resumeDateHuman },
  accountsService,
  onClose,
  onDone,
  onReject,
}: {
  offer: GetPausePlanOfferResponse;
  accountsService: Pick<AccountsService, 'acceptPausePlanOffer'>;
  onClose: () => void;
  onDone: () => void;
  onReject: () => void;
}) {
  const [loading, error, submit] = useSubmit(async () => {
    await accountsService.acceptPausePlanOffer({
      confirmResumeDateHuman: resumeDateHuman,
    });
    onDone();
  });

  return (
    <Dialog
      onClose={loading ? undefined : onClose}
      title="Pause your plan"
      buttons={
        <>
          <Button
            size="large"
            label="Cancel plan"
            variant="secondary"
            onClick={onReject}
          />
          <Button
            size="large"
            icon={IconPause}
            label="Pause plan"
            variant="primary"
            onClick={submit}
            loading={loading}
          />
        </>
      }
    >
      <TextRegularLight>
        Don’t have any projects on currently? Put your plan on pause so you can
        focus on securing projects.
      </TextRegularLight>
      <div className={styles.pauseBox}>
        <Rows spacing={2}>
          <TextRegular>Activate Pause</TextRegular>
          <TextSmallLight>
            Starts on <TextSmall tag="span">{currentPeriodEndHuman}</TextSmall>{' '}
            (next billing cycle)
            <br />
            and will resume on{' '}
            <TextSmall tag="span">{resumeDateHuman}</TextSmall>.
          </TextSmallLight>
        </Rows>
      </div>
      <RedErrorText error={error} />
    </Dialog>
  );
}

export function CancelDiscountOfferDialog({
  offers,
  accountsService,
  onClose,
  onDone,
  onUpsell,
  onReject,
}: {
  offers: GetCancelCouponOfferResponse;
  accountsService: Pick<AccountsService, 'acceptCancelCouponOffer'>;
  onClose: () => void;
  onDone: () => void;
  onUpsell: (state: AcceptPlanDialogUrlState) => void;
  onReject: () => void;
}) {
  const [selected, setSelected] = React.useState<'coupon' | 'mty'>(() =>
    offers.discountOfferPercent != null ? 'coupon' : 'mty',
  );

  const [loading, error, submit] = useSubmit(async () => {
    if (selected === 'coupon') {
      await accountsService.acceptCancelCouponOffer();
      onDone();
    } else if (selected === 'mty') {
      if (!offers.mtyOffer) throw new Error('No mty offer');
      onUpsell({
        planKey: offers.mtyOffer.planKey,
        couponCode: offers.mtyOffer.couponCode,
      });
    } else {
      throw new UnreachableError(selected);
    }
  });

  return (
    <Dialog
      onClose={loading ? undefined : onClose}
      title="A cheeky one-time offer"
      buttons={
        <>
          <Button
            size="large"
            label="Cancel plan"
            variant="secondary"
            onClick={onReject}
          />
          <Button
            size="large"
            label="Claim offer instead"
            variant="primary"
            onClick={submit}
            loading={loading}
          />
        </>
      }
    >
      <InvisibleForm loading={loading} onSubmit={submit}>
        <Rows spacing={24}>
          <TextRegularLight>
            {offers.userFirstName} we really don’t want to lose you with how
            much we have coming up. Can we tempt you?
          </TextRegularLight>
          <div className={styles.discounts}>
            {offers.discountOfferPercent != null && (
              <DiscountCard
                title="Reduced plan price"
                subtitle={`We can offer you ${offers.discountOfferPercent}% off your current plan`}
                percent={offers.discountOfferPercent}
                selected={selected === 'coupon'}
                onClick={() => setSelected('coupon')}
              />
            )}
            {offers.mtyOffer != null && (
              <DiscountCard
                title="Switch to yearly"
                subtitle={`Swap from monthly to yearly and receive the whole year at ${offers.mtyOffer.discountPercent}% off!`}
                percent={offers.mtyOffer.discountPercent}
                selected={selected === 'mty'}
                onClick={() => setSelected('mty')}
              />
            )}
          </div>
          <TextSmallLight color="grey">
            {selected === 'coupon' ? (
              <>
                Discount starts on your next bill (
                <TextSmall tag="span" color="black">
                  {offers.currentPeriodEndHuman}
                </TextSmall>
                ).
              </>
            ) : (
              <>
                Discount starts{' '}
                <TextSmall tag="span" color="black">
                  today
                </TextSmall>
                .
              </>
            )}
          </TextSmallLight>
          <RedErrorText error={error} />
        </Rows>
      </InvisibleForm>
    </Dialog>
  );
}

function DiscountCard({
  title,
  subtitle,
  percent,
  selected,
  onClick,
}: {
  title: string;
  subtitle: string;
  percent: number;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <div
      className={classNames(styles.discountCard, selected && styles.isSelected)}
      onClick={onClick}
    >
      <div className={styles.discountCardBorder} />
      <div className={styles.discountCardInner}>
        <Checkbox value={selected} label="" type="radio" />
        <Rows spacing={2}>
          <TextRegular>{title}</TextRegular>
          <TextSmallLight color="grey">{subtitle}</TextSmallLight>
        </Rows>
        <DiscountChip percent={percent} tooltip="What a bargain!" />
      </div>
    </div>
  );
}

function CancelNowOnMountDialog({
  accountsService,
  onDone,
}: {
  accountsService: Pick<AccountsService, 'cancelPlanNow'>;
  onDone: () => void;
}) {
  const [result, setResult] = React.useState<
    CancelPlanNowResponse | undefined
  >();
  const [error, setError] = React.useState<unknown>();

  React.useEffect(
    () => {
      accountsService.cancelPlanNow().then(setResult).catch(setError);
    }, // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  if (result) {
    return (
      <Dialog
        title="Plan cancelled"
        onClose={onDone}
        buttons={
          <Button
            size="large"
            label="Close"
            variant="primary"
            onClick={onDone}
          />
        }
      >
        <Rows spacing={24}>
          <TextRegularLight>
            Thanks for your feedback, we’re sorry to see you go.
          </TextRegularLight>
          <TextRegularLight>
            You will still have access to your current plan until the end of
            your billing period on{' '}
            <TextRegular tag="span">{result.currentPeriodEndHuman}</TextRegular>
            .
          </TextRegularLight>
        </Rows>
      </Dialog>
    );
  }
  if (error) {
    return <ErrorDialog error={error} />;
  }
  return <LoadingDialog title="Cancelling plan" />;
}

function LoadingDialog({ title }: { title: string }) {
  return (
    <Dialog title={title}>
      <LoadingDots aspectRatio={4} />
    </Dialog>
  );
}

function ErrorDialog({ error }: { error: unknown }) {
  return (
    <Dialog title="Error cancelling plan">
      <TextRegularLight>
        There was an error cancelling your plan. Please refresh the page to try
        again.
      </TextRegularLight>
      <RedErrorText error={error} />
    </Dialog>
  );
}

export function ChurnFeedbackDialog({
  accountsService,
  onClose,
  onDone,
}: {
  accountsService: Pick<AccountsService, 'saveChurnFeedback'>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = React.useState('');
  const [feedback, setFeedback] = React.useState('');
  const [loading, error, submit] = useSubmit(async () => {
    await accountsService.saveChurnFeedback({ reason, feedback });
    onDone();
  });

  return (
    <Dialog
      title="Why are you leaving?"
      onClose={loading ? undefined : onClose}
    >
      <InvisibleForm loading={loading} onSubmit={submit}>
        <Rows spacing={24}>
          <TextRegularLight>
            Please take moment to help us improve Relume Library by telling us
            why you're cancelling your subscription.
          </TextRegularLight>
          <Rows spacing={8}>
            <TextSmallLight color="grey">
              What’s your main reason for cancelling?
            </TextSmallLight>
            {CANCELATION_REASONS.map((r) => (
              <Checkbox
                key={r}
                label={r}
                value={r === reason}
                onChange={() => setReason(r)}
                type="radio"
                labelColor="inherit"
              />
            ))}
          </Rows>
          <Rows spacing={8}>
            <TextSmallLight color="grey">
              What could we have done better to keep you as a customer?
            </TextSmallLight>
            <TextRegularLight>
              <Textarea
                value={feedback}
                onChange={setFeedback}
                placeholder="How could we have improved the service for you..."
                allowNewlines
                minHeight={112}
                maxHeight={112}
              />
            </TextRegularLight>
          </Rows>
          <RedErrorText error={error} />
          <Inline spacing={8}>
            <SpacerGrow />
            <Button
              size="large"
              label="Keep my plan"
              variant="secondary"
              onClick={onClose}
            />
            <Button
              size="large"
              label="Cancel plan"
              variant="primary"
              type="submit"
              disabled={!reason}
              loading={loading}
            />
          </Inline>
        </Rows>
      </InvisibleForm>
    </Dialog>
  );
}

const CANCELATION_REASONS = [
  `I don't have any projects right now`,
  `I only needed it for a one-off project`,
  `Didn't work as expected`,
  `Didn't fit into my workflow`,
  `Too expensive`,
  `I found a better alternative`,
  `I don’t use Figma or Webflow`,
  `Too difficult to use`,
  `Support was slow or poor`,
  `Other`,
];
